import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Box,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import type { SetupValue } from '../../data/types';
import type { SetupField } from '../../domain/setup/fieldTypes';
import AddressAutocompleteField from '../../../components/AddressAutocompleteField';
import ColourFieldInput from './ColourFieldInput';

interface Props {
  field: SetupField;
  value: SetupValue | undefined;
  prefill: SetupValue | undefined;
  error?: string;
  readOnly: boolean;
  onChange: (value: SetupValue) => void;
  onBlur?: () => void;
}

export default function SetupFieldInput({
  field,
  value,
  prefill,
  error,
  readOnly,
  onChange,
  onBlur,
}: Props) {
  const edited = prefill !== undefined && value !== prefill;
  const markers =
    edited && !readOnly ? (
      <Tooltip title="Put the pre-filled value back">
        <IconButton
          size="small"
          aria-label={`Reset ${field.label}`}
          onClick={() => onChange(prefill as SetupValue)}
          sx={{ p: 0.25 }}
        >
          <RestartAltIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    ) : null;

  if (field.kind === 'switch') {
    return (
      <Box sx={{ gridColumn: field.wide ? '1 / -1' : undefined }}>
        <FormControlLabel
          control={
            <Switch
              checked={value === true}
              disabled={readOnly || field.key === 'inv.entityDocs'}
              onChange={(event) => onChange(event.target.checked)}
            />
          }
          label={field.label}
        />
        {field.help && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 6 }}>
            {field.help}
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" component="div" sx={{ ml: 6 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ ml: 6 }}>{markers}</Box>
      </Box>
    );
  }

  const stringValue = String(value ?? '');
  if (field.kind === 'colour') {
    return (
      <ColourFieldInput
        field={field}
        value={stringValue}
        error={error}
        readOnly={readOnly}
        markers={markers}
        onChange={onChange}
      />
    );
  }
  if (field.kind === 'address') {
    return (
      <Box sx={{ gridColumn: field.wide ? '1 / -1' : undefined }}>
        <AddressAutocompleteField
          fullWidth
          size="small"
          label={field.label}
          required={field.required}
          value={stringValue}
          disabled={readOnly}
          error={Boolean(error)}
          helperText={error ?? field.help}
          onChange={onChange}
          onBlur={onBlur}
        />
        <Box sx={{ mt: 0.5 }}>{markers}</Box>
      </Box>
    );
  }
  return (
    <Box sx={{ gridColumn: field.wide || field.kind === 'textarea' ? '1 / -1' : undefined }}>
      <TextField
        fullWidth
        size="small"
        select={field.kind === 'select'}
        type={field.kind === 'date' ? 'date' : 'text'}
        multiline={field.kind === 'textarea'}
        minRows={field.kind === 'textarea' ? 2 : undefined}
        label={field.label}
        required={field.required}
        value={stringValue}
        disabled={readOnly}
        error={Boolean(error)}
        helperText={error ?? field.help}
        inputProps={field.kind === 'number' ? { inputMode: 'numeric' } : undefined}
        InputLabelProps={field.kind === 'date' ? { shrink: true } : undefined}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      >
        {field.options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ mt: 0.5 }}>{markers}</Box>
    </Box>
  );
}
